/**
 * Scroll a paginated list container back to the top after creating a new item.
 */
export function scrollListToTop(container: HTMLElement | null | undefined): void {
  resolveScroller(container)?.scrollTo?.({ top: 0, left: 0 });
}

/**
 * The element that actually scrolls. On a ListPage the region a page holds a ref to
 * (`ListPage.Body`) is not itself a scroller - the table's own box inside it is
 * (`data-slot="table-scroll"`, see components/ui/ListPage.tsx) - so look for that first.
 */
function resolveScroller(container: HTMLElement | null | undefined): HTMLElement | null | undefined {
  if (!container) return container;
  if (container.getAttribute?.('data-slot') === 'table-scroll') return container;
  const inner = container.querySelector?.<HTMLElement>('[data-slot="table-scroll"]');
  return inner ?? container;
}

/**
 * Scroll the list container when it is scrollable; otherwise bring a fallback element
 * (e.g. the list header) into view at the top of the viewport.
 */
export function scrollListToTopWithFallback(
  container: HTMLElement | null | undefined,
  fallbackElement?: HTMLElement | null | undefined
): void {
  const scroller = resolveScroller(container);
  if (scroller && scroller.scrollHeight > scroller.clientHeight) {
    scroller.scrollTo({ top: 0, left: 0 });
    return;
  }
  fallbackElement?.scrollIntoView({ block: 'start' });
}

/** Run scroll reset after the DOM has painted refreshed list content. */
export function scrollListToTopAfterPaint(
  container: HTMLElement | null | undefined,
  fallbackElement?: HTMLElement | null | undefined
): void {
  requestAnimationFrame(() => {
    if (fallbackElement) {
      scrollListToTopWithFallback(container, fallbackElement);
    } else {
      scrollListToTop(container);
    }
  });
}
